// PROTOTYPE — test-run (dry run) results drawer. Per-step layout mirroring the
// v2 run detail, but clearly marked "DRY RUN — no action performed". Shows each
// brick's resolved inputs, would-be outputs, and validation errors.

import {Box, Drawer, IconButton, Typography, Chip} from "@mui/material";
import {
    Close as CloseIcon, BoltOutlined as BoltIcon,
    CheckCircle as OkIcon, Cancel as FailIcon, RemoveCircleOutline as SkipIcon,
    ScienceOutlined as TestIcon,
} from "@mui/icons-material";
import {RTL} from "../../../cache/CacheRtl";
import {borders, colors} from "../../ui/colors";

const BRAND = "#01BBA7";
const STATUS = {
    SUCCESS: {label: "תקין", color: "#10B981", bg: "#DCFCE7", Icon: OkIcon},
    FAILED: {label: "בעיה", color: "#EF4444", bg: "#FEE2E2", Icon: FailIcon},
    SKIPPED: {label: "דולג", color: "#94A3B8", bg: "#F1F5F9", Icon: SkipIcon},
    // Filter condition not met — the branch stops here (not an error).
    STOPPED: {label: "נעצר בתנאי", color: "#F59E0B", bg: "#FEF3C7", Icon: SkipIcon},
};

function KV({rows}) {
    const entries = Object.entries(rows || {});
    if (!entries.length) return <Typography sx={{fontSize: "12px", color: colors.textMuted}}>—</Typography>;
    return (
        <Box sx={{display: "grid", gridTemplateColumns: "auto 1fr", gap: "2px 12px"}}>
            {entries.map(([k, v]) => (
                <Box key={k} sx={{display: "contents"}}>
                    <Typography sx={{fontSize: "12px", fontFamily: "monospace", color: colors.textMuted, direction: "ltr", textAlign: "left"}}>{k}</Typography>
                    <Typography sx={{fontSize: "12px", fontWeight: 600, wordBreak: "break-all"}}>{String(v)}</Typography>
                </Box>
            ))}
        </Box>
    );
}

export default function TestRunDrawer({open, result, onClose}) {
    return (
        <Drawer anchor="left" open={open} onClose={onClose} PaperProps={{sx: {width: {xs: "100%", sm: 460}}}}>
            <RTL>
                <Box dir="rtl" sx={{height: "100%", display: "flex", flexDirection: "column", bgcolor: colors.white}}>
                    {/* Header */}
                    <Box sx={{px: 2, py: 1.5, borderBottom: borders.default, bgcolor: colors.bg,
                              display: "flex", alignItems: "center", justifyContent: "space-between"}}>
                        <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                            <TestIcon sx={{color: BRAND}}/>
                            <Typography sx={{fontSize: "15px", fontWeight: 700, color: colors.textPrimary}}>הרצת בדיקה</Typography>
                        </Box>
                        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small"/></IconButton>
                    </Box>

                    {/* Dry-run banner */}
                    <Box sx={{px: 2, py: 1, bgcolor: "#FEF3C7", borderBottom: `1px solid #F59E0B33`}}>
                        <Typography sx={{fontSize: "12px", color: "#92400E", fontWeight: 600}}>
                            הרצה יבשה — שום פעולה לא בוצעה בפועל (לא נוצר מתאמן, לא נשלח מייל)
                        </Typography>
                    </Box>

                    {result && (
                        <Box sx={{flex: 1, overflowY: "auto", p: 2, display: "flex", flexDirection: "column", gap: 1.5}}>
                            {/* Trigger */}
                            <Box sx={{p: 1.5, border: borders.default, borderRadius: borders.radius.md, bgcolor: `${BRAND}0D`}}>
                                <Box sx={{display: "flex", alignItems: "center", gap: 1, mb: 0.5}}>
                                    <BoltIcon sx={{fontSize: 16, color: BRAND}}/>
                                    <Typography sx={{fontSize: "13px", fontWeight: 700}}>{result.trigger.label}</Typography>
                                    {!result.trigger.configured && <Chip label="לא הוגדר" size="small" sx={{height: 20, fontSize: "10px", bgcolor: "#FEE2E2", color: "#EF4444"}}/>}
                                </Box>
                                <Typography sx={{fontSize: "11px", color: colors.textMuted, mb: 0.5}}>נתונים לדוגמה מהמקור:</Typography>
                                <KV rows={result.trigger.data}/>
                            </Box>

                            {/* Steps */}
                            {result.steps.length === 0 && (
                                <Typography sx={{fontSize: "13px", color: colors.textMuted, textAlign: "center", py: 3}}>
                                    אין שלבים לבדוק — הוסף פעולות לזרימה
                                </Typography>
                            )}
                            {result.steps.map((s, i) => {
                                const m = STATUS[s.status] || STATUS.SKIPPED;
                                return (
                                    <Box key={s.id} sx={{border: borders.default, borderRadius: borders.radius.md, overflow: "hidden"}}>
                                        <Box sx={{display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 1, bgcolor: colors.bg}}>
                                            <m.Icon sx={{fontSize: 20, color: m.color}}/>
                                            <Typography sx={{fontSize: "13px", fontWeight: 600, flex: 1}}>{i + 1}. {s.label}</Typography>
                                            <Chip label={m.label} size="small" sx={{height: 22, fontSize: "11px", fontWeight: 700, bgcolor: m.bg, color: m.color}}/>
                                        </Box>
                                        {s.status !== "SKIPPED" && (
                                            <Box sx={{px: 1.5, py: 1, display: "flex", flexDirection: "column", gap: 1}}>
                                                {s.errors?.length > 0 && (
                                                    <Box>
                                                        {s.errors.map((e, j) => (
                                                            <Typography key={j} sx={{fontSize: "12px", color: "#EF4444", fontWeight: 600}}>• {e}</Typography>
                                                        ))}
                                                    </Box>
                                                )}
                                                {s.inputs?.length > 0 && (
                                                    <Box>
                                                        <Typography sx={{fontSize: "11px", color: colors.textMuted, mb: 0.5}}>קלט שחושב:</Typography>
                                                        <KV rows={Object.fromEntries(s.inputs.map(inp => [inp.key + (inp.required ? " *" : ""), inp.value]))}/>
                                                    </Box>
                                                )}
                                                {s.status === "SUCCESS" && s.outputs && (
                                                    <Box>
                                                        <Typography sx={{fontSize: "11px", color: colors.textMuted, mb: 0.5}}>פלט שהיה נוצר:</Typography>
                                                        <KV rows={s.outputs}/>
                                                    </Box>
                                                )}
                                            </Box>
                                        )}
                                        {s.status === "SKIPPED" && (
                                            <Box sx={{px: 1.5, py: 1}}><Typography sx={{fontSize: "12px", color: colors.textMuted}}>דולג — שלב קודם נכשל</Typography></Box>
                                        )}
                                    </Box>
                                );
                            })}
                        </Box>
                    )}
                </Box>
            </RTL>
        </Drawer>
    );
}
